/**
 * ECharts heatmap 包装 — 用于 Complaints 页"车型 × 一级问题"矩阵。
 */
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';

import { useBreakpoint } from '@/lib/breakpoints';

interface HeatmapMatrixProps {
  /** Y 轴标签（一般是车型） */
  yLabels: string[];
  /** X 轴标签（一般是问题分类） */
  xLabels: string[];
  /** [yIndex, xIndex, value] 数组 */
  data: Array<[number, number, number]>;
  /** 最大值（色阶上限）；不传默认取 data 最大 */
  max?: number;
  height?: number;
  onCellClick?: (yLabel: string, xLabel: string, value: number) => void;
}

export default function HeatmapMatrix({
  yLabels,
  xLabels,
  data,
  max,
  height,
  onCellClick,
}: HeatmapMatrixProps) {
  const { isMobile } = useBreakpoint();
  const calcMax = max ?? Math.max(1, ...data.map((d) => d[2]));
  // 9.2f: mobile 缩高 + 字号;矩阵保横滚(Content overflow-x),不转 card
  const resolvedHeight = height ?? (isMobile ? 340 : 480);

  const option: EChartsOption = {
    tooltip: {
      position: 'top',
      confine: true,
      formatter: (params) => {
        const p = params as unknown as { value: [number, number, number] };
        const [xi, yi, v] = p.value;
        return `${yLabels[yi]} · ${xLabels[xi]}<br/>投诉数：<b>${v}</b>`;
      },
    },
    grid: {
      left: isMobile ? 70 : 100,
      right: 16,
      top: 30,
      bottom: 60,
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: xLabels,
      splitArea: { show: true },
      axisLabel: { rotate: isMobile ? 45 : 30, fontSize: isMobile ? 9 : 11, color: '#475569' },
    },
    yAxis: {
      type: 'category',
      data: yLabels,
      splitArea: { show: true },
      axisLabel: { fontSize: isMobile ? 9 : 11, color: '#475569' },
    },
    visualMap: {
      min: 0,
      max: calcMax,
      calculable: true,
      orient: 'horizontal',
      left: 'center',
      bottom: 0,
      itemWidth: isMobile ? 12 : 20,
      inRange: { color: ['#F8FAFC', '#FEF3C7', '#F59E0B', '#EA580C', '#DC2626'] },
      text: ['多', '少'],
      textStyle: { fontSize: isMobile ? 10 : 11, color: '#64748B' },
    },
    series: [
      {
        type: 'heatmap',
        // ECharts expects [x, y, value]
        data: data.map(([yi, xi, v]) => [xi, yi, v]),
        label: { show: !isMobile, fontSize: 10, color: '#1F2937' },
        emphasis: { itemStyle: { shadowBlur: 8, shadowColor: 'rgba(0,0,0,0.4)' } },
      },
    ],
  };

  return (
    <ReactECharts
      option={option}
      style={{ height: resolvedHeight }}
      onEvents={
        onCellClick
          ? {
              click: (e: unknown) => {
                const ev = e as { value: [number, number, number] };
                const [xi, yi, v] = ev.value;
                onCellClick(yLabels[yi], xLabels[xi], v);
              },
            }
          : undefined
      }
    />
  );
}
